import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { edicionActiva } from '@/config/ediciones';
import {
  fetchEvents,
  fetchCampeonatos,
  fetchEventDetail,
  urlDeCampeonato,
  slugify,
  type Evento,
  type EventsResponse,
  type Campeonato,
  type PodioRunner,
  type Intento,
  type PruebaCombinada,
  type EventDetail,
} from '@/lib/api';
import { esUrlEA, fetchEventsEA, fetchEventDetailEA } from '@/lib/eaLive';
import { BombaEspejo, type EstadoBomba } from '@/lib/eaMirror';
import { esUrlWA, fetchEventsWA, fetchEventDetailWA, espPorUnidadWA, esKeyResumenWA } from '@/lib/waLive';
import { ResumenJornadaModal } from '@/components/ResumenJornadaModal';
import { espPorUnidadEA } from '@/lib/eaLive';
import type { FilaPodio } from '@/lib/podios/data';
import { esConcurso } from '@/lib/podios/cruce';
import { pruebaKey } from '@/lib/minimas';
import { elegirVecinos } from '@/lib/podios/vecinos';
import { ensureAnonAuth } from '@/lib/authPublic';
import { SKIN_IDS, SKIN_LABEL, type SkinId } from '@/store/SkinContext';
import { SKIN_ICON, INSTAGRAM_URL } from '@/components/AppShell';
import { Icon } from '@/components/Icon';
import { FichaVivoCard } from '@/components/FichaVivoCard';
import { CARD_DIMS, type Formato, type PosV, type PosH } from '@/components/AtletaCard';
import { Preview } from '@/components/Preview';
import { ImageEditor } from '@/components/ImageEditor';
import { coverCrop, optimizeImage, zoomSliderToFactor } from '@/lib/imagen';
import { TelegramPublishModal } from '@/components/TelegramPublishModal';

/** Normaliza para búsqueda insensible a mayúsculas y acentos. */
function norm(s: string): string {
  return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Iconos inline (para no depender del componente Icon). Instagram (feather)
 *  para el compartir nativo — en móvil el menú del sistema lleva a Stories;
 *  y download para descargar. */
function IconInstagram({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}
function IconDownload({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
/** Glifo de WhatsApp (relleno con currentColor). */
function IconWhatsApp({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.29-.15-1.71-.84-1.98-.94-.27-.1-.46-.15-.65.15-.19.29-.75.94-.92 1.13-.17.19-.34.22-.63.07-.29-.15-1.22-.45-2.33-1.44-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.65-1.57-.89-2.15-.24-.56-.48-.48-.65-.49h-.56c-.19 0-.51.07-.77.36-.27.29-1.01.99-1.01 2.41 0 1.42 1.04 2.8 1.18 2.99.15.19 2.04 3.12 4.95 4.37.69.3 1.23.48 1.65.61.69.22 1.32.19 1.82.12.56-.08 1.71-.7 1.95-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.34zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.42 9.42 0 0 1-1.44-5.02c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.98 6.67 2.77a9.38 9.38 0 0 1 2.76 6.68c0 5.2-4.24 9.44-9.44 9.44zm8.04-17.49A11.34 11.34 0 0 0 12.05 0C5.8 0 .72 5.08.72 11.32c0 1.99.52 3.94 1.51 5.66L.63 24l7.18-1.88a11.3 11.3 0 0 0 5.24 1.34h.01c6.24 0 11.32-5.08 11.32-11.32 0-3.03-1.18-5.87-3.32-8.02z" />
    </svg>
  );
}

/** Dominio público que se enlaza en el texto compartido. */
const WEB_URL = 'resultados.mundoatletismo.es';

/** Emoji del puesto. Medalla (🥇🥈🥉) y "tableta" del 4º (🍫) SOLO cuando la
 *  competición reparte podio oficial (campeonato de España individual, vía
 *  setDeCampeonato → conMedallas); en el resto, aplauso neutro. */
function emojiPuesto(puesto: number, conMedallas: boolean): string {
  if (conMedallas) {
    if (puesto === 1) return '🥇';
    if (puesto === 2) return '🥈';
    if (puesto === 3) return '🥉';
    if (puesto === 4) return '🍫';
  }
  if (puesto >= 1 && puesto <= 8) return '👏';
  return '🏃';
}

/** Línea extra según el logro de MÁS peso del protagonista (mismas etiquetas
 *  que pinta la ficha; prioridad de arriba abajo). '' si no hay ninguno. */
function lineaLogro(etiquetas: string[] | undefined): string {
  const et = etiquetas ?? [];
  const tiene = (p: string) => et.some((t) => t.startsWith(p));
  if (tiene('R.Esp')) return '🇪🇸 ¡Y encima RÉCORD DE ESPAÑA!';
  if (tiene('RC')) return '🏆 ¡Récord del campeonato!';
  const at = et.find((t) => t.includes('all-time'));
  if (at) return `📊 ${at}`; // ya viene como "#N all-time"
  if (tiene('Mín')) return '✅ ¡Mínima conseguida!';
  if (tiene('PB')) return '💪 ¡Nueva marca personal!';
  if (tiene('SB')) return '📈 Mejor marca de la temporada';
  return '';
}

/** Texto que acompaña a la imagen al compartir el resultado del protagonista. */
function textoWhatsApp(
  competicion: string, puesto: string, marca: string,
  etiquetas: string[] | undefined, conMedallas: boolean,
): string {
  const n = parseInt(puesto, 10);
  const pos = Number.isFinite(n) ? `${n}º` : (puesto || '').trim();
  const emoji = Number.isFinite(n) ? emojiPuesto(n, conMedallas) : '';
  const comp = (competicion || 'mi competición').trim();
  const base = `¡Mira mi resultado en ${comp}! Fui ${pos} con una marca de ${marca} ${emoji}`.replace(/\s+/g, ' ').trim();
  const logro = lineaLogro(etiquetas);
  return [`${base}.`, logro, `Comparte tu resultado entrando en: ${WEB_URL}`].filter(Boolean).join('\n');
}

/** Cartel propio de cada campeonato. Viven en el bucket S3 de conersys, bajo
 *  `home/<CODE>.png`, con un índice `home/home_images_list.json` de los que
 *  tienen imagen (descubierto 2026-07-12 decompilando el Blazor de rfealive:
 *  ChampionshipImagesUrl → .../home/). El bucket es público y NO bloquea por
 *  origen/referer (verificado), así que se sirve DIRECTO desde S3 — sin proxy
 *  (antes /api/cartel apuntaba a rfealive.es/images/home, host equivocado que
 *  sí bloquea hotlink y caía a la genérica RFEA). Sirve a coste cero para
 *  nosotros. Nota: la extensión es siempre .png aunque el contenido sea JPG en
 *  algunos; al navegador le da igual en un <img>. Si el cto no tiene imagen, la
 *  petición falla y el onError deja solo el recuadro con el nombre. */
function cartelSrc(code: string): string {
  return `https://conersys-live-d.s3.dualstack.eu-west-3.amazonaws.com/home/${encodeURIComponent(code)}.png`;
}

/** Nombre corto de campeonato para las tarjetas de acceso rápido (Riki
 *  2026-07-11; el desplegable conserva el nombre oficial completo):
 *  "Campeonato de España" → "Cto. España", "sub-23" → "U23", "Nacionales" →
 *  "Nac.", "por Clubes" → "clubs", "Primera/Segunda División" → "1º/2º",
 *  "División de Honor" → "DH". */
function acortarCampeonato(titulo: string): string {
  return (titulo || '')
    .replace(/campeonatos? de espa[ñn]a/gi, 'Cto. España')
    .replace(/\bnacionales\b/gi, 'Nac.')
    .replace(/\bsub[- ]?(\d{2})\b/gi, 'U$1')
    .replace(/\bpor clubes\b/gi, 'clubs')
    .replace(/\bdivisi[óo]n de honor\b/gi, 'DH')
    .replace(/\bprimera divisi[óo]n\b/gi, '1º')
    .replace(/\bsegunda divisi[óo]n\b/gi, '2º')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Colores de la píldora de ESTADO de una prueba (paso 2). Estados de la
 *  fuente (2026-07-11): Oficial (acabada) → acento; Empezada → texto normal;
 *  Lista de Salida → atenuado; Horario (ni alineaciones) → apagado. */
function estadoPillStyle(estado: string): React.CSSProperties {
  const e = norm(estado);
  const base: React.CSSProperties = {
    fontFamily: 'var(--ld-font-mono)', fontSize: 10, letterSpacing: '0.06em',
    textTransform: 'uppercase', borderRadius: 999, padding: '2px 8px',
    whiteSpace: 'nowrap', border: '1px solid var(--ld-line, #444)',
  };
  if (e.includes('oficial')) return { ...base, color: 'var(--ld-accent)', borderColor: 'var(--ld-accent)' };
  if (e.includes('empezada')) return { ...base, color: 'var(--ld-text)' };
  if (e.includes('lista')) return { ...base, color: 'var(--ld-text-muted, #999)' };
  return { ...base, color: 'var(--ld-text-muted, #999)', opacity: 0.6 }; // Horario y resto
}

/** Clave de orden por fecha+hora (mayor = más reciente). Igual que Home. */
function tsClave(e: Evento): number {
  const f = (e.fecha || '').trim();
  let y = 0, mo = 0, d = 0;
  let m = f.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; }
  else {
    m = f.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (m) { d = +m[1]; mo = +m[2]; y = +m[3]; }
  }
  const hm = (e.hora || '').match(/(\d{1,2}):(\d{2})/);
  return ((y * 12 + mo) * 31 + d) * 1440 + (hm ? +hm[1] : 0) * 60 + (hm ? +hm[2] : 0);
}

/** Lista completa de una prueba (campo entero). Cae al podio si el backend
 *  aún no envía `resultados` (fuente antigua). */
function campoDe(ev: Evento): PodioRunner[] {
  return ev.resultados && ev.resultados.length ? ev.resultados : ev.podio;
}

/** Corre el cruce (PB/SB/récord/mínima/líder/#all-time) sobre el CAMPO
 *  COMPLETO de una prueba y devuelve los corredores con etiquetas/flags. Es
 *  importante cruzar todo el campo (no solo 3): el puesto #N all-time cuenta
 *  cuántos atletas mejoran, y el gate de mínimas mira todo el grupo.
 *  `runners` llega ya resuelto (clasificación del detalle RES en rfea.es, o
 *  campoDe(ev) en el resto de fuentes). */
async function cruzarCampo(runners: PodioRunner[], ev: Evento, data: EventsResponse): Promise<PodioRunner[]> {
  if (!runners.length) return runners;
  try {
    const { construirIndices, analizarCompeticion } = await import('@/lib/podios/data');
    const filas: FilaPodio[] = runners.map((p) => ({
      nombre: p.nombre,
      atleta: p.atleta ?? p.nombre,
      pais: p.pais,
      prueba: ev.nombre,
      marca: p.marca,
      fechaNac: p.fechaNac,
      viento: p.viento,
      fecha: ev.fecha,
      lugar: data.sede ?? '',
    }));
    const idx = await construirIndices(filas);
    const res = analizarCompeticion(filas, idx);
    let conEtiquetas = runners.map((p, i) => ({ ...p, etiquetas: res[i].etiquetas, flags: res[i].out.flags }));
    // Etiqueta "Récord del campeonato" (R.Cto, Riki 2026-07-10): SOLO si esta
    // competición ES un Campeonato de España individual — el candado vive en
    // setDeCampeonato(); si el título no casa, recordDelCampeonato → null y
    // aquí no pasa nada. Decisión Riki 2026-07-11 (revierte la de ayer):
    // RC se muestra TAMBIÉN junto a R.Esp — quien bate el récord de España
    // en el campeonato luce ambas píldoras, aunque una implique la otra.
    try {
      const { recordDelCampeonato, bateRecordCampeonato } = await import('@/lib/podios/recordCampeonato');
      const rec = recordDelCampeonato(data.competicion, ev.nombre);
      if (rec) {
        conEtiquetas = conEtiquetas.map((p) => {
          if (!bateRecordCampeonato(rec, p.marca, p.viento, ev.nombre)) return p;
          return { ...p, etiquetas: ['RC', ...(p.etiquetas ?? [])] };
        });
      }
    } catch (e) {
      console.warn('[atleta] récord de campeonato no disponible:', e);
    }
    return conEtiquetas;
  } catch (e) {
    console.warn('[atleta] cruce no disponible:', e);
    return runners;
  }
}

/** Campeonatos visibles en el modo ADMIN (/admin). De momento solo el Cto.
 *  España sub-20 (pruebas de la publicación en Telegram); el Absoluto entra
 *  solo en cuanto la RFEA lo publique en la lista (ya casan los patrones). */
const ADMIN_CAMPEONATOS: RegExp[] = [
  /campeonato de espa[ñn]a sub[-\s]?20/i,
  /campeonato de espa[ñn]a(\s+absoluto)?\s*$/i,
];

/** Modo Rieti (European Athletics): inyecta fuente de datos + competición fija.
 *  undefined = la vista funciona EXACTAMENTE igual que hasta ahora. */
export interface RietiMode {
  fetchEvents: () => Promise<EventsResponse>;
  fetchEventDetail: (key: string) => Promise<EventDetail>;
  campeonato: Campeonato;
}

/** Cto. de Europa absoluto Birmingham 2026 (European Athletics). El sondeo de
 *  candidatos probaría EU18CH26 (Rieti) antes que ECH26, así que el code va
 *  forzado. Corre EN EL NAVEGADOR (API pública con CORS; Cloudflare bloquea las
 *  IPs de GCP). Se usa tanto en el acceso rápido de /resultados como en /Birmingham. */
const BIRMINGHAM_URL = 'https://live.european-athletics.com/birmingham-2026?code=ECH26';

export function AtletaEnVivo({ rieti, admin, birmingham }: { rieti?: RietiMode; admin?: boolean; birmingham?: boolean } = {}) {
  // Estilo (skin) de la TARJETA. La web va SIEMPRE en retro (ver el
  // `skin-retro` del contenedor); esto solo cambia la imagen que se genera.
  const [cardSkin, setCardSkin] = useState<SkinId>('retro');
  const [authReady, setAuthReady] = useState(false);
  const [campeonatos, setCampeonatos] = useState<Campeonato[] | null>(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EventsResponse | null>(null);

  // ESPEJO de EA (Birmingham 2026): la pestaña bombea los feeds a Firestore
  // vía /api/ea-mirror para que el motor en GCP (bloqueado por Cloudflare)
  // pueda leer. Con Wake Lock para que la pantalla no se apague; aun así la
  // pestaña debe quedarse VISIBLE (en segundo plano el navegador ralentiza
  // los timers). Solo admin + fuente "ea".
  const bombaRef = useRef<BombaEspejo | null>(null);
  const wakeLockRef = useRef<{ release(): Promise<void>; addEventListener?: unknown } | null>(null);
  const [espejo, setEspejo] = useState<EstadoBomba | null>(null);

  const toggleEspejo = async () => {
    if (bombaRef.current?.estado.activa) {
      bombaRef.current.parar();
      void wakeLockRef.current?.release().catch(() => undefined);
      wakeLockRef.current = null;
      return;
    }
    let code: string | null = null;
    for (const e of data?.eventos ?? []) {
      const m = /^EA:([^:]+):/.exec(e.detalleKey ?? '');
      if (m) { code = m[1]; break; }
    }
    if (!code) return;
    const bomba = new BombaEspejo(
      code,
      data?.competicion ?? code,
      async () => (await fetchEventsEA(`https://live.european-athletics.com/live?code=${code}`))
        .eventos.map((e) => ({
          key: e.detalleKey ?? '', nombre: e.nombre, ronda: e.ronda ?? '',
          fecha: e.fecha ?? '', hora: e.hora ?? '', estado: e.estado ?? '',
        })),
      setEspejo,
    );
    bombaRef.current = bomba;
    bomba.arrancar();
    try {
      // Wake Lock: mantiene la PANTALLA encendida mientras la pestaña esté
      // visible. Si el navegador no lo soporta, la bomba funciona igual.
      wakeLockRef.current = await (navigator as Navigator & {
        wakeLock?: { request(t: 'screen'): Promise<{ release(): Promise<void> }> };
      }).wakeLock?.request('screen') ?? null;
    } catch { wakeLockRef.current = null; }
  };

  // El Wake Lock se LIBERA solo al ocultar la pestaña: re-adquirirlo al
  // volver mientras la bomba siga activa.
  useEffect(() => {
    const re = () => {
      if (document.visibilityState === 'visible' && bombaRef.current?.estado.activa) {
        void (navigator as Navigator & {
          wakeLock?: { request(t: 'screen'): Promise<{ release(): Promise<void> }> };
        }).wakeLock?.request('screen')
          .then((wl) => { wakeLockRef.current = wl; })
          .catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', re);
    return () => {
      document.removeEventListener('visibilitychange', re);
      bombaRef.current?.parar();
      void wakeLockRef.current?.release().catch(() => undefined);
    };
  }, []);

  const [query, setQuery] = useState('');
  // Página actual del listado de pruebas (paso 2). Se resetea al cambiar la
  // búsqueda o la competición.
  const [pagina, setPagina] = useState(0);
  const [prueba, setPrueba] = useState<Evento | null>(null);
  const [campo, setCampo] = useState<PodioRunner[] | null>(null);
  const [cruzando, setCruzando] = useState(false);
  const [protagIdx, setProtagIdx] = useState<number | null>(null);
  // "Solo yo": muestra únicamente al atleta buscado, sin los vecinos de
  // contexto (Riki 2026-07-14). Aplica igual en resultado y en lista de salida.
  const [soloYo, setSoloYo] = useState(false);
  // Paso 3: al elegirte, la lista de atletas colapsa a un resumen (Riki
  // 2026-07-14) para no ocupar tanto; "Cambiar" la vuelve a abrir.
  const [listaColapsada, setListaColapsada] = useState(false);
  // Paso 2: al elegir prueba, la tarjeta de búsqueda colapsa a un resumen
  // (Riki 2026-07-14); "Cambiar" reabre el buscador y la lista.
  const [pruebaColapsada, setPruebaColapsada] = useState(false);
  // Modo del Paso 2 (Riki 2026-07-14): "resultado" (pruebas competidas) o
  // "salida" (listas de salida de eventos aún por disputarse).
  const [modo, setModo] = useState<'resultado' | 'salida'>('resultado');

  // Ajustes de la ficha. Decisión Riki 2026-07-11: SOLO formato vertical
  // 9:16 (Stories); fuera el 16:9 y, con él, los botones Izq./Der. (posH
  // solo aportaba en apaisado). Quedan como constantes por si algún día se
  // recupera la opción.
  const formato: Formato = '9:16';
  const posH: PosH = 'izquierda';
  const [posicion, setPosicion] = useState<PosV>('abajo');
  const [escala, setEscala] = useState(1);
  const [degradado, setDegradado] = useState(100);
  const [opacidad, setOpacidad] = useState(100);
  // Color del scrim: null = el de la skin (var(--ld-bg)). Se puede forzar para
  // dar contraste sobre fotos claras (p. ej. skin Lane, casi blanca).
  const [scrimColor, setScrimColor] = useState<string | null>(null);
  // Color "auto" del scrim = el --ld-bg del ESTILO ELEGIDO para la tarjeta (no
  // el de la web, que va fija en retro). Se mide con un sondeo oculto con esa
  // clase skin-*, así se mantiene en sync con el CSS.
  const skinBg = useMemo(() => {
    if (typeof window === 'undefined') return '#000000';
    const probe = document.createElement('div');
    probe.className = rieti ? 'skin-rieti' : `skin-${cardSkin}`;
    probe.style.display = 'none';
    document.body.appendChild(probe);
    const bg = getComputedStyle(probe).getPropertyValue('--ld-bg').trim() || '#000000';
    probe.remove();
    return bg;
  }, [cardSkin]);
  const [fotoDataUrl, setFotoDataUrl] = useState<string | undefined>(undefined);
  const [fotoSrc, setFotoSrc] = useState<string | undefined>(undefined);
  // Encuadre de la foto de fondo (motor central ImageEditor): offX/offY 0-100
  // y zoom como slider 0-100. Antes el recorte era centrado fijo (50,50) sin
  // zoom; ahora el usuario mueve/acerca la foto (Riki 2026-07-13).
  const [fotoX, setFotoX] = useState(50);
  const [fotoY, setFotoY] = useState(50);
  const [fotoZoom, setFotoZoom] = useState(0);
  // Editor de encuadre PLEGABLE: se abre al subir foto (encuadre inicial) y con
  // el botón "Encuadrar imagen"; se cierra con "Listo". Evita tener siempre el
  // recuadro grande ocupando sitio (Riki 2026-07-13).
  const [encuadreAbierto, setEncuadreAbierto] = useState(false);
  const [busy, setBusy] = useState(false);
  // Imagen ya rasterizada (File PNG) para compartir por el menú nativo. Se
  // genera de forma anticipada porque navigator.share exige activación de
  // usuario: si se generase dentro del click (await), iOS Safari la rechaza.
  const [shareFile, setShareFile] = useState<File | null>(null);
  // Móvil: en el paso final el botón de descargar pasa a icono y se añade el
  // de compartir (menú nativo). matchMedia reactivo.
  const [esMovil, setEsMovil] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 700px)');
    const on = () => setEsMovil(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  // Caché de detalles RES<Key> (por competición cargada). Evita repetir la
  // descarga entre la búsqueda por atleta, el conteo exacto y pickPrueba.
  const detCache = useRef(new Map<string, Promise<EventDetail>>());
  // Nº exacto de atletas por detalleKey (se va rellenando con cada detalle
  // bajado; hasta entonces el listado enseña "3+").
  const [conteos, setConteos] = useState<Record<string, number>>({});
  // Búsqueda por atleta afinada: keys de las unidades donde APARECE el
  // atleta buscado (no todas las rondas de su prueba). `cargando` mientras
  // se bajan los detalles (entretanto se enseñan todas las candidatas).
  const [busqAtleta, setBusqAtleta] = useState<{ keys: Set<string>; cargando: boolean } | null>(null);
  // Autocompletado (Riki 2026-07-11): a partir del 5º carácter, sugerencias
  // "atleta - prueba - ronda" (una por ronda en la que aparece). El nombre se
  // muestra bien acentuado; el matching es insensible a tildes/mayúsculas
  // (norm() a ambos lados). Click → abre la prueba y preselecciona al atleta.
  const [sugerencias, setSugerencias] = useState<Array<{ nombre: string; athId: string; ev: Evento }>>([]);
  // Modo ADMIN: prueba/serie cuyo modal "Publicar en Telegram" está abierto,
  // y en qué modo arranca (serie / clasificados de la ronda).
  const [tgEvento, setTgEvento] = useState<{ ev: Evento; modo: 'serie' | 'clasificados'; canal: 'telegram' | 'whatsapp' } | null>(null);

  /** ADMIN: true si la RONDA de esta serie (no final, no combinada) está
   *  COMPLETA (todas sus series en Oficial) → los "clasificados de la ronda"
   *  ya se pueden publicar; el listado enseña el botón "Q/q" (Riki
   *  2026-07-21: que se vea desde fuera, sin entrar en la serie). */
  function rondaClasificable(ev: Evento): boolean {
    if (!admin || !data || !ev.detalleKey) return false;
    const r = (ev.ronda || '').toLowerCase();
    if ((/final/.test(r) && !/semi/.test(r)) || /combinada/.test(r) || !r.trim()) return false;
    // Quita la letra de grupo de concurso EA ("Clasificación A/B") para que A y
    // B agrupen como hermanas de la misma ronda (ver parseRonda en el modal).
    const base = (ev.ronda || '').replace(/\d+\s*\/\s*\d+/, '').trim().replace(/\s+[A-Za-z]$/, '').trim().toLowerCase();
    const m = (ev.ronda || '').match(/\d+\s*\/\s*(\d+)/);
    const total = m ? +m[1] : null;
    const vistas = new Set<string>();
    const hermanas: Evento[] = [];
    for (const e of data.eventos) {
      if (e.nombre !== ev.nombre || !e.detalleKey || vistas.has(e.detalleKey)) continue;
      if ((e.ronda || '').replace(/\d+\s*\/\s*\d+/, '').trim().toLowerCase() !== base) continue;
      vistas.add(e.detalleKey);
      hermanas.push(e);
    }
    if (!hermanas.length || (total !== null && hermanas.length < total)) return false;
    return hermanas.every((e) => /oficial/i.test(e.estado || ''));
  }

  function detalleDe(key: string): Promise<EventDetail> {
    let p = detCache.current.get(key);
    if (!p) {
      // Keys "EA:...": detalle desde el navegador (ver fetchEventsEA).
      p = (rieti ? rieti.fetchEventDetail(key)
        : key.startsWith('EA:') ? fetchEventDetailEA(key)
        : key.startsWith('WA:') ? fetchEventDetailWA(key)
        : fetchEventDetail(key)).then((d) => {
        if (d.clasificacion.length) {
          setConteos((c) => (key in c ? c : { ...c, [key]: d.clasificacion.length }));
        }
        return d;
      });
      detCache.current.set(key, p);
    }
    return p;
  }

  // Tarjeta del paso 3 ("Encuéntrate"): al elegir prueba se desliza hasta ella.
  const paso3Ref = useRef<HTMLDivElement | null>(null);
  // Tarjeta del paso 4 ("Tu publicación"): al elegir atleta se desliza hasta ella.
  const paso4Ref = useRef<HTMLDivElement | null>(null);

  function pickAtleta(i: number) {
    setProtagIdx(i);
    setListaColapsada(true);
    // La tarjeta del paso 4 no existe hasta que `seleccion` deja de ser null.
    window.setTimeout(() => paso4Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  }

  /** Vuelta al inicio (click en el logo del encabezado, Riki 2026-07-11):
   *  resetea todo al estado de partida SIN recargar la página. */
  function volverInicio() {
    setData(null);
    setUrl('');
    setQuery('');
    setPrueba(null);
    setCampo(null);
    setProtagIdx(null);
    setPagina(0);
    setError(null);
    setBusqAtleta(null);
    setSugerencias([]);
    setConteos({});
    detCache.current = new Map();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const { w, h } = CARD_DIMS[formato];
  const protagName = campo && protagIdx !== null ? campo[protagIdx].nombre : 'atleta';
  const categoria = prueba ? [prueba.nombre, prueba.ronda].filter(Boolean).join(' · ') : '';

  // Recorta la foto al recuadro del formato (cover) para que la captura no la
  // deforme. Igual que FichaAtleta.
  useEffect(() => {
    if (!fotoDataUrl) { setFotoSrc(undefined); return; }
    let cancel = false;
    coverCrop(fotoDataUrl, w, h, fotoX, fotoY, zoomSliderToFactor(fotoZoom))
      .then((src) => { if (!cancel) setFotoSrc(src); })
      .catch(() => { if (!cancel) setFotoSrc(fotoDataUrl); });
    return () => { cancel = true; };
  }, [fotoDataUrl, w, h, fotoX, fotoY, fotoZoom]);

  function onPickFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const raw = typeof reader.result === 'string' ? reader.result : undefined;
      if (!raw) return;
      // Motor central: optimiza (downscale + JPEG) al subir; reencuadre a
      // centro para empezar limpio con cada foto nueva.
      const optimizada = await optimizeImage(raw).catch(() => raw);
      setFotoX(50); setFotoY(50); setFotoZoom(0);
      setEncuadreAbierto(true); // abre el editor para el encuadre inicial
      setFotoDataUrl(optimizada);
    };
    reader.readAsDataURL(f);
  }

  // scale 2 → PNG a 2x (p. ej. 2160×3840 en 9:16): el texto lo rasteriza
  // html2canvas y a 1x sale más blando que el preview del navegador.
  async function renderCanvasBlob(): Promise<Blob> {
    if (!cardRef.current) throw new Error('No hay imagen que generar');
    // html2canvas se carga BAJO DEMANDA (import dinámico): así no lastra el
    // bundle inicial de /resultados, solo pesa al generar la imagen.
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(cardRef.current, { width: w, height: h, scale: 2, backgroundColor: null, useCORS: true });
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('No se pudo generar la imagen');
    return blob;
  }

  const nombreArchivo = () => `${slugify(protagName)}_${formato.replace(':', 'x')}.png`;

  async function descargar() {
    setBusy(true);
    setError(null);
    try {
      const blob = shareFile ?? (await renderCanvasBlob());
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = nombreArchivo();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Menú nativo de compartir (WhatsApp/Instagram/…). Usa el File ya generado
  // (shareFile) para no perder la activación de usuario en iOS. Misma pauta que
  // AthleteShareModal de rankings-espana-advance.
  const canShareFiles =
    typeof navigator !== 'undefined' &&
    !!navigator.canShare &&
    !!shareFile &&
    navigator.canShare({ files: [shareFile] });

  // Compartir por WhatsApp con texto personalizado del resultado. Móvil (con
  // compartir de ficheros): menú nativo con imagen + texto. PC: descarga la
  // imagen y abre WhatsApp Web con el texto (se adjunta la imagen a mano).
  async function compartirWhatsApp() {
    const prot = campo && protagIdx !== null ? campo[protagIdx] : null;
    let conMedallas = false;
    try {
      // Mismo candado que el récord de campeonato: medalla SOLO en campeonatos
      // de España individuales (no clubes/federaciones/equipos/master).
      const { setDeCampeonato } = await import('@/lib/podios/recordCampeonato');
      conMedallas = !!setDeCampeonato(data?.competicion ?? '');
    } catch { /* si falla, sin medallas */ }
    const texto = textoWhatsApp(
      data?.competicion ?? '', prot?.puesto ?? '', prot?.marca ?? '', prot?.etiquetas, conMedallas,
    );
    // Si el navegador soporta compartir FICHEROS (todo móvil y algunos PC:
    // Windows Chrome/Edge, Safari), va la imagen + el texto JUNTOS por el menú
    // nativo. Si no (WhatsApp Web clásico no acepta imagen por URL), se descarga
    // la imagen y se abre wa.me solo con el texto.
    if (canShareFiles && shareFile) {
      try { await navigator.share({ files: [shareFile], text: texto }); }
      catch { /* cancelado por el usuario */ }
      return;
    }
    await descargar();
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
  }

  // Sesión anónima + lista de competiciones de rfealive.es.
  useEffect(() => {
    if (rieti) { setAuthReady(true); return; } // European Athletics: API pública, sin auth
    let cancel = false;
    ensureAnonAuth()
      .then(() => { if (!cancel) setAuthReady(true); })
      .catch((e) => { if (!cancel) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancel = true; };
  }, [rieti]);

  useEffect(() => {
    if (!authReady) return;
    if (rieti) { setCampeonatos([rieti.campeonato]); return; }
    let cancel = false;
    fetchCampeonatos().then((c) => {
      if (cancel) return;
      // Modo ADMIN: solo los campeonatos con publicación en Telegram (sub-20
      // hoy; el Absoluto entrará solo cuando aparezca en la lista).
      setCampeonatos(admin ? c.filter((x) => ADMIN_CAMPEONATOS.some((re) => re.test(x.titulo))) : c);
    });
    return () => { cancel = true; };
  }, [authReady, rieti, admin]);

  // Modo Rieti: competición fija -> carga automática (sin selector).
  useEffect(() => {
    if (rieti && authReady && !data && !loading) void cargar('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rieti, authReady]);

  // Modo Birmingham (/Birmingham): autocarga del Europeo vía eaLive, igual que
  // pulsar su acceso rápido en /resultados. Sin selector, con portada propia.
  useEffect(() => {
    if (birmingham && authReady && !data && !loading) void cargar(BIRMINGHAM_URL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [birmingham, authReady]);

  // EA (Europeo): al cargar, arranca el velo (scrim) al 50% para que se vea el
  // fondo del Alexander Stadium detrás (el asset ya viene lavado en crema). Una
  // sola vez; después el slider de opacidad manda (Santi 2026-08-10).
  const opacEaRef = useRef(false);
  useEffect(() => {
    if (data?.fuente === 'ea' && !opacEaRef.current) { opacEaRef.current = true; setOpacidad(50); }
  }, [data]);

  // Capa de contexto del PANEL (Riki 2026-07-22): si venimos del panel de un
  // campeonato, la URL trae ?camp=<rfeaCode> y autocargamos ese campeonato, para
  // que la herramienta abra YA filtrada por él (efecto "integrada en el panel"),
  // en vez de en su estado base con el selector vacío. Sin el parámetro (entrada
  // directa a /resultados) funciona como siempre.
  const campCtxRef = useRef(false);
  useEffect(() => {
    if (rieti || birmingham || !authReady || campCtxRef.current) return;
    const code = new URLSearchParams(window.location.search).get('camp');
    if (code) {
      campCtxRef.current = true;
      onPickCampeonato(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, rieti]);

  async function cargar(u: string) {
    const target = u.trim();
    if (!rieti && !target) return;
    setError(null);
    setLoading(true);
    setData(null);
    setPrueba(null);
    setCampo(null);
    setProtagIdx(null);
    setPagina(0);
    detCache.current = new Map();
    setConteos({});
    setBusqAtleta(null);
    setSugerencias([]);
    try {
      let r: EventsResponse;
      if (rieti) {
        r = await rieti.fetchEvents();
      } else if (esUrlEA(target)) {
        // European Athletics: scrapeo EN EL NAVEGADOR (su API tiene CORS
        // abierto pero Cloudflare bloquea las IPs de GCP — 2026-07-28).
        r = await fetchEventsEA(target);
      } else if (esUrlWA(target)) {
        // World Athletics: GraphQL público del calendario, también en el
        // navegador por consistencia con EA (2026-07-29).
        r = await fetchEventsWA(target);
      } else {
        await ensureAnonAuth();
        r = await fetchEvents(target);
      }
      setData(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  function onPickCampeonato(code: string) {
    if (!code) return;
    const u = urlDeCampeonato(code);
    setUrl(u);
    void cargar(u);
  }

  /** Abre una prueba. Con `athIdPreferido` (autocompletado), además
   *  preselecciona a ese atleta y desliza directo a su publicación. */
  async function pickPrueba(ev: Evento, athIdPreferido?: string) {
    setPrueba(ev);
    setPruebaColapsada(true); // colapsa el Paso 2 al elegir prueba
    setProtagIdx(null);
    setCampo(null);
    setCruzando(true);
    // Desliza hasta el paso 3 en cuanto React lo pinte (la tarjeta no existe
    // hasta que `prueba` deja de ser null, de ahí el pequeño retardo).
    window.setTimeout(() => paso3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    try {
      // Modo LISTA DE SALIDA: no hay resultados que cruzar; el campo se arma de
      // la ALINEACIÓN (calle + nombre + club) del detalle RES, ordenada por calle.
      if (modo === 'salida') {
        const det = ev.detalleKey ? await detalleDe(ev.detalleKey) : null;
        const runners: PodioRunner[] = (det?.alineacion ?? [])
          .map((a) => ({ puesto: a.calle ?? '', nombre: a.nombre, club: a.club, marca: '', athId: a.athId }))
          .sort((x, y) => (parseInt(x.puesto, 10) || 99) - (parseInt(y.puesto, 10) || 99));
        setCampo(runners);
        if (athIdPreferido) {
          const idx = runners.findIndex((p) => p.athId === athIdPreferido);
          if (idx >= 0) pickAtleta(idx);
        }
        return;
      }
      let base = campoDe(ev);
      let intentos: Record<string, Intento[]> = {};
      let combinada: Record<string, PruebaCombinada[]> = {};
      // rfea.es: el SCH (→ ev.resultados) solo publica el TOP-3 por evento
      // (verificado 2026-07-10), así que el campo completo se baja del
      // detalle RES<Key> para CUALQUIER tipo de prueba, no solo concursos.
      // Si el detalle falla o trae menos (nunca visto), se queda con base.
      if (ev.detalleKey) {
        const det = await detalleDe(ev.detalleKey);
        if (det.clasificacion.length > base.length) base = det.clasificacion;
        intentos = det.intentos ?? {};
        combinada = det.combinada ?? {};
      }
      let runners = await cruzarCampo(base, ev, data as EventsResponse);
      // Modo Rieti: mantenemos PB/SB (de la BBDD) pero quitamos las píldoras de
      // mínima (nacional / europea sub-18), redundantes en un campeonato de Europa.
      if (rieti) {
        runners = runners.map((p) => ({ ...p, etiquetas: (p.etiquetas ?? []).filter((t) => !t.startsWith('Mín')) }));
      }
      // Concursos (rfea.es): adjunta la serie de intentos por athId.
      if (esConcurso(ev.nombre) && Object.keys(intentos).length) {
        runners = runners.map((p) => (p.athId && intentos[p.athId] ? { ...p, intentos: intentos[p.athId] } : p));
      }
      // Combinadas (hept/decatlón): adjunta el desglose por disciplina por athId.
      if (Object.keys(combinada).length) {
        runners = runners.map((p) => (p.athId && combinada[p.athId] ? { ...p, combinada: combinada[p.athId] } : p));
      }
      setCampo(runners);
      if (athIdPreferido) {
        const idx = runners.findIndex((p) => p.athId === athIdPreferido);
        if (idx >= 0) pickAtleta(idx);
      }
    } finally {
      setCruzando(false);
    }
  }

  // BÚSQUEDA POR ATLETA AFINADA (Riki 2026-07-10): la inscripción solo dice
  // la PRUEBA (eve), no la serie, así que el filtro grueso enseña TODAS las
  // rondas del 100 m. Aquí se bajan los detalles RES de las rondas candidatas
  // (con caché) y nos quedamos con las unidades donde el athId del buscado
  // APARECE de verdad. Mientras cargan se enseñan todas las candidatas y la
  // lista se estrecha sola al terminar.
  useEffect(() => {
    const toks = norm(query).split(/\s+/).filter(Boolean);
    if (!data?.inscritos?.length || !toks.length) { setBusqAtleta(null); setSugerencias([]); return; }
    const matched = data.inscritos.filter((a) => {
      // Cada palabra escrita debe coincidir con el INICIO de alguna palabra del
      // nombre (Riki 2026-07-14): permite saltarse nombres intermedios
      // ("Julián Martínez" encuentra a "Julián Antonio Martínez") y evita
      // coincidencias a mitad de palabra ("Ana" NO saca a "Mariana", pero sí a
      // "Anacleto").
      const nombreToks = norm(a.nombre).split(/\s+/).filter(Boolean);
      return toks.every((t) => nombreToks.some((nt) => nt.startsWith(t)));
    });
    if (!matched.length) { setBusqAtleta(null); setSugerencias([]); return; }
    const eves = new Set(matched.map((a) => a.eve));
    // ESTADOS de la fuente (Riki 2026-07-11): "Horario" = ni siquiera hay
    // listas de salida → NO se sabe quién compite en esa unidad, fuera de la
    // búsqueda por atleta. "Lista de Salida" / "Empezada" / "Oficial" → sí
    // (por alineación o por resultados).
    const candidatas = data.eventos.filter(
      (e) => e.detalleKey && eves.has(e.detalleKey.split(':')[2]) && !/^horario$/i.test((e.estado || '').trim()),
    );
    const todas = new Set(candidatas.map((e) => e.detalleKey as string));
    // Con la query aún poco concreta ("pa" casa con media inscripción) no se
    // bajan detalles: filtro grueso y listo. Se afina cuando quedan pocos.
    const REFINAR_MAX = 15;
    if (matched.length > REFINAR_MAX) { setBusqAtleta({ keys: todas, cargando: false }); setSugerencias([]); return; }
    const athIds = new Set(matched.map((a) => a.athId).filter(Boolean) as string[]);
    let cancel = false;
    setBusqAtleta({ keys: todas, cargando: true });
    // Sugerencias INSTANTÁNEAS (Riki 2026-07-11: "tarda un poco"): se pintan
    // ya con lo que sabemos sin red (atleta × rondas de su prueba, del SCH
    // cargado) y se AFINAN cuando lleguen los detalles RES (fuera las rondas
    // donde no aparece). Durante 1-2 s puede listar una ronda de más.
    const conQuery5 = norm(query).replace(/\s+/g, '').length >= 5;
    if (conQuery5) {
      const inmediatas: Array<{ nombre: string; athId: string; ev: Evento }> = [];
      const vistosIni = new Set<string>();
      for (const a of matched) {
        if (!a.athId) continue;
        for (const e of candidatas) {
          if ((e.detalleKey as string).split(':')[2] !== a.eve) continue;
          // Máx UNA sugerencia por atleta×ronda (Riki 2026-07-11): ALLINS
          // puede traer inscripciones duplicadas del mismo atleta.
          const sig = `${a.athId}|${e.detalleKey}`;
          if (vistosIni.has(sig)) continue;
          vistosIni.add(sig);
          inmediatas.push({ nombre: a.nombre, athId: a.athId, ev: e });
        }
      }
      inmediatas.sort((x, y) => tsClave(x.ev) - tsClave(y.ev));
      setSugerencias(inmediatas.slice(0, 8));
    } else {
      setSugerencias([]);
    }
    Promise.all(candidatas.map(async (e) => {
      const d = await detalleDe(e.detalleKey as string);
      // El atleta "está" en la unidad si aparece en la clasificación
      // (Empezada/Oficial) o en la alineación (Lista de Salida). Si el
      // detalle no llegó (fallo de red / backend sin `salida` aún), NO se
      // descarta la ronda: mejor enseñar de más que esconder su serie.
      const sinDatos = !d.clasificacion.length && !(d.salida?.length);
      const tiene = sinDatos
        || d.clasificacion.some((p) => p.athId && athIds.has(p.athId))
        || (d.salida ?? []).some((id) => athIds.has(id));
      return { ev: e, det: d, tiene };
    })).then((rs) => {
      if (cancel) return;
      setBusqAtleta({ keys: new Set(rs.filter((r) => r.tiene).map((r) => r.ev.detalleKey as string)), cargando: false });
      // Afinado de sugerencias con los detalles ya resueltos: una entrada
      // por atleta×ronda en la que APARECE de verdad. Máx 10.
      if (!conQuery5) { setSugerencias([]); return; }
      const sugs: Array<{ nombre: string; athId: string; ev: Evento }> = [];
      const vistosFin = new Set<string>();
      for (const a of matched) {
        if (!a.athId) continue;
        for (const r of rs) {
          if ((r.ev.detalleKey as string).split(':')[2] !== a.eve) continue;
          // Máx UNA sugerencia por atleta×ronda (dedupe de inscripciones).
          const sig = `${a.athId}|${r.ev.detalleKey}`;
          if (vistosFin.has(sig)) continue;
          // En la unidad si está en la clasificación (Empezada/Oficial) o en
          // la alineación (Lista de Salida). Detalle vacío → regla tolerante.
          const sinDatos = !r.det.clasificacion.length && !(r.det.salida?.length);
          if (sinDatos || r.det.clasificacion.some((p) => p.athId === a.athId) || (r.det.salida ?? []).includes(a.athId)) {
            vistosFin.add(sig);
            sugs.push({ nombre: a.nombre, athId: a.athId, ev: r.ev });
          }
        }
      }
      sugs.sort((x, y) => tsClave(x.ev) - tsClave(y.ev));
      setSugerencias(sugs.slice(0, 8));
    });
    return () => { cancel = true; };
  }, [data, query]);

  // Pruebas con resultados, dedup + orden por fecha, filtradas por búsqueda.
  // Filtro de ESPAÑOLES en fuentes internacionales (EA/WA, 2026-07-29):
  // key→false = unidad sin españoles (prueba sin ESP, o ronda a la que ya no
  // han pasado) → fila DESACTIVADA (y botones de publicar en el admin). Se
  // aplica IGUAL en admin (/telegram) y en público (/resultados, /Birmingham):
  // el público solo debe ver lo español (2026-08-09). El mapa llega en diferido
  // (EA mira el detalle de las rondas posteriores); mientras tanto todo queda
  // habilitado.
  const [espMap, setEspMap] = useState<Record<string, boolean>>({});
  // Modal del resumen de jornada (fuente WA → Instagram).
  const [resumenKey, setResumenKey] = useState<string | null>(null);
  useEffect(() => {
    setEspMap({});
    if (!data || (data.fuente !== 'ea' && data.fuente !== 'wa')) return;
    let cancel = false;
    const calc = data.fuente === 'ea'
      ? espPorUnidadEA(data.eventos, detalleDe)
      : espPorUnidadWA(data.eventos);
    calc.then((m) => { if (!cancel) setEspMap(m); })
      .catch((e) => console.warn('[esp] filtro de españoles falló', e));
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const pruebas = useMemo(() => {
    if (!data) return [] as Evento[];
    const vistos = new Set<string>();
    const out: Evento[] = [];
    // "salida" = evento con lista de salida y aún NO competido (estado "Lista de
    // Salida"); en cuanto empieza/finaliza/revisa, sale de este modo → no se
    // puede generar la lista de salida (Riki 2026-07-14).
    const esSalida = (est: string) => /salida/i.test(est || '');
    // Sub-evento de una prueba COMBINADA (p. ej. "Salto de Longitud Heptatlón
    // Mujeres", "100 m Decatlón Hombres"): NO debe poder elegirse en el Paso 2;
    // solo la prueba CONJUNTA ("Decatlón/Heptatlón/Pentatlón…", con su
    // puntuación). Se detecta porque el nombre lleva la palabra "-atlón" pero
    // NO como primera palabra (la prueba global la lleva al principio). Vale
    // para RFEA y Rieti, cuyos nombres traducidos también acaban en "-atlón"
    // (Riki 2026-07-21).
    const esSubComb = (nombre: string): boolean =>
      norm(nombre).split(/\s+/).filter(Boolean).findIndex((w) => /atlon/.test(w)) > 0;
    for (const e of data.eventos) {
      // En ADMIN los sub-eventos SÍ se listan: son el respaldo manual para
      // publicar en Telegram la prueba suelta de la combinada.
      if (!admin && esSubComb(e.nombre)) continue;
      const sig = `${norm(e.nombre)}|${e.ronda}|${e.hora}|${e.fecha}`;
      if (vistos.has(sig)) continue;
      vistos.add(sig);
      if (modo === 'salida') { if (esSalida(e.estado)) out.push(e); }
      // European Athletics no trae podio en el horario (el detalle se pide
      // bajo demanda): en modo resultado se filtra por ESTADO, no por campo.
      // "En curso" es el estado WA/EA de una unidad con resultados aún no
      // oficiales (waLive:348) — sin él, el grupo B de la altura M (J2) no
      // aparecía en el manual hasta ser oficial (2026-08-07).
      else if (campoDe(e).length > 0 || ((data.fuente === 'ea' || data.fuente === 'wa') && /oficial|empezada|en curso/i.test(e.estado))) out.push(e);
    }
    // Orden: agrupa por prueba y, DENTRO de cada una, FINAL → SEMIFINAL →
    // eliminatorias/series (si la final está disponible, la gente crea con ella,
    // no con series más antiguas). Los grupos van por su tiempo más reciente
    // (Riki 2026-07-14).
    const rondaPrio = (ronda: string): number => {
      const r = norm(ronda);
      if (/semi/.test(r)) return 1;   // antes que "final" (contiene "final")
      if (/final/.test(r)) return 0;
      return 2;                        // eliminatoria / serie / ronda / etc.
    };
    const grupoTs = new Map<string, number>();
    for (const e of out) {
      const k = norm(e.nombre);
      grupoTs.set(k, Math.max(grupoTs.get(k) ?? -Infinity, tsClave(e)));
    }
    out.sort((a, b) => {
      const ka = norm(a.nombre), kb = norm(b.nombre);
      const ga = grupoTs.get(ka) ?? 0, gb = grupoTs.get(kb) ?? 0;
      if (ga !== gb) return gb - ga;              // prueba más reciente primero
      if (ka !== kb) return ka < kb ? -1 : 1;     // empate de tiempo: agrupa por nombre
      return rondaPrio(a.ronda) - rondaPrio(b.ronda) || tsClave(b) - tsClave(a);
    });
    const toks = norm(query).split(/\s+/).filter(Boolean);
    if (!toks.length) return out;
    // Clave canónica de una prueba (sin género): "110 metros vallas" / "110 mv"
    // / "110mv" / "110 m vallas" → "110mv". Permite buscar la prueba de varias
    // formas sin precisión milimétrica (Riki 2026-07-14).
    const claveP = (nombre: string) =>
      pruebaKey(
        (nombre || '')
          // género irrelevante; y "m" o CUALQUIER prefijo de "metros" (m, me,
          // met, metr, metro, metros) ≡ "m", para que la búsqueda no parpadee
          // mientras escribes "…metros" (Riki 2026-07-14).
          .replace(/\b(hombres?|mujeres?|varones|masculino|femenino)\b/gi, ' ')
          .replace(/\bm(?:e(?:t(?:r(?:o(?:s)?)?)?)?)?\b/gi, 'm'),
      );
    const qKey = claveP(query);
    // Búsqueda de prueba palabra a palabra por PREFIJO: cada palabra escrita
    // debe ser prefijo de alguna palabra del evento. La "m" del evento se
    // expande a "metros" (única abreviatura en los nombres), así "400 metros va"
    // encuentra "400 m vallas" y no parpadea a media palabra, sin falsos
    // positivos tipo "martillo"→"100 m" (Riki 2026-07-14).
    const tokP = (s: string): string[] =>
      norm(s)
        // género NORMALIZADO a "hombres"/"mujeres" y CONSERVADO: así "…vallas ho"
        // casa (ho→hombres) y "…mujeres" NO casa el evento de hombres.
        .replace(/\b(hombres?|masculino|masc|varones?)\b/g, 'hombres')
        .replace(/\b(mujeres?|femenino|fem)\b/g, 'mujeres')
        .replace(/(\d)\.(\d)/g, '$1$2')
        .split(/\s+/)
        .filter(Boolean)
        .map((t) => (t === 'm' ? 'metros' : t));
    const qTok = tokP(query);
    // Género del texto ('h'/'m'/null), para no cruzar géneros en la coincidencia
    // por CLAVE (que sí ignora el género) — Riki 2026-07-14.
    const genero = (s: string): 'h' | 'm' | null => {
      const n = norm(s);
      if (/\b(hombres?|masculino|masc|varones?)\b/.test(n)) return 'h';
      if (/\b(mujeres?|femenino|fem)\b/.test(n)) return 'm';
      return null;
    };
    const qGen = genero(query);
    // Filtra por nombre de PRUEBA o por ATLETA (simultáneo): se muestra la
    // prueba si todos los tokens aparecen en su nombre, o si algún atleta de la
    // prueba los contiene todos (así el atleta se busca a sí mismo y salen sus
    // pruebas). LIMITACIÓN rfea.es: el SCH solo publica el top-3 por evento
    // (2026-07-10), así que aquí la búsqueda por atleta solo ve el podio; el
    // campo completo llega al elegir la prueba (detalle RES<Key>). Mejorable a
    // futuro buscando en las inscripciones (ALLINS).
    // Inscripciones (rfea.es): encuentra la prueba de un atleta que NO fue
    // top-3 (caso Paula Crespo Ferrero, 100 m elims, 2026-07-10). El efecto
    // busqAtleta calcula las unidades: primero todas las rondas de su prueba
    // (eve de la inscripción) y, al llegar los detalles RES, solo las series
    // donde aparece su athId.
    return out.filter((e) => {
      // Prueba por PREFIJO de palabras (ver tokP): "400 metros va" → "400 m
      // vallas", "salto alt" → "salto de altura", sin parpadear.
      if (qTok.length && qTok.every((q) => tokP(e.nombre).some((ev) => ev.startsWith(q)))) return true;
      // Coincidencia por CLAVE canónica (cubre decathlon↔decatlón, 1.500↔1500,
      // marcha…): "110mv" encuentra "110 metros vallas" (Riki 2026-07-14).
      if (qKey.length >= 2 && claveP(e.nombre).startsWith(qKey) && (!qGen || !genero(e.nombre) || qGen === genero(e.nombre))) return true;
      if (busqAtleta && e.detalleKey && busqAtleta.keys.has(e.detalleKey)) return true;
      return campoDe(e).some((p) => {
        const an = norm(`${p.nombre} ${p.atleta ?? ''}`);
        return toks.every((t) => an.includes(t));
      });
    });
  }, [data, query, busqAtleta, modo]);

  // Paginación del paso 2: máx 8 pruebas por página (pedido Riki 2026-07-10).
  const PRUEBAS_POR_PAGINA = 5;
  const totalPaginas = Math.max(1, Math.ceil(pruebas.length / PRUEBAS_POR_PAGINA));
  // Clamp: si el filtro deja menos páginas que la actual, no mostrar vacío.
  const pag = Math.min(pagina, totalPaginas - 1);
  const pruebasVisibles = pruebas.slice(pag * PRUEBAS_POR_PAGINA, (pag + 1) * PRUEBAS_POR_PAGINA);

  // TAMAÑO por defecto (Riki 2026-07-11): el mayor valor del slider
  // [100%..140%] con el que el NOMBRE del protagonista cabe en UNA línea.
  // Se mide con canvas (misma fuente/tamaños que FichaVivoCard: nombre 38px,
  // puesto 58px con minWidth 56, marca 50px, Q/q 50px mono, padding 56×2 y
  // huecos de 16), sin renderizar nada. El usuario puede moverlo después.
  useEffect(() => {
    if (!campo || protagIdx === null) return;
    const p = campo[protagIdx];
    if (!p) return;
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return;
    const raiz = cardRef.current ?? document.documentElement;
    const css = getComputedStyle(raiz);
    const fDisplay = css.getPropertyValue('--ld-font-display').trim() || 'sans-serif';
    const fMono = css.getPropertyValue('--ld-font-mono').trim() || 'monospace';
    const mide = (texto: string, px: number, familia: string, peso = 800) => {
      ctx.font = `${peso} ${px}px ${familia}`;
      return ctx.measureText(texto).width;
    };
    let optima = 1;
    for (let i = 8; i >= 0; i--) {
      const e = Math.round((1 + i * 0.05) * 100) / 100; // 1.40 → 1.00
      const puestoW = Math.max(56 * e, mide(String(p.puesto ?? ''), 58 * e, fDisplay));
      const marcaW = mide(p.marca || '', 50 * e, fDisplay);
      const qualiW = p.quali ? mide(p.quali, 50 * e, fMono, 700) + 16 * e : 0;
      const disponible = w - 112 - puestoW - marcaW - qualiW - 32 * e;
      const nombreW = mide((p.nombre || '').toUpperCase(), 38 * e, fDisplay);
      if (nombreW <= disponible * 0.98) { optima = e; break; }
    }
    setEscala(optima);
  }, [campo, protagIdx, w]);

  // Vecinos (protagonista + hasta 2 de contexto) una vez elegido el atleta.
  // REGLA TOP-3 (Riki 2026-07-10): si el protagonista es 1º, 2º o 3º, la
  // ficha muestra SIEMPRE el podio entero (1-2-3), no la ventana de vecinos
  // (que para el 3º daría 2-3-4).
  const seleccion = useMemo(() => {
    if (!campo || protagIdx === null) return null;
    // "Solo yo": una sola fila (el atleta buscado), sin vecinos de contexto.
    if (soloYo) {
      return { runners: [campo[protagIdx]], protagonistaEnFila: 0 };
    }
    if (protagIdx < 3) {
      const indices = [0, 1, 2].filter((k) => k < campo.length);
      return { runners: indices.map((k) => campo[k]), protagonistaEnFila: protagIdx };
    }
    const { indices, protagonistaEnFila } = elegirVecinos(campo.length, protagIdx);
    return { runners: indices.map((k) => campo[k]), protagonistaEnFila };
  }, [campo, protagIdx, soloYo]);

  // Props de la ficha, compartidas por el preview, el nodo de captura y el
  // OVERLAY de textos del encuadre (para ver cómo queda). null si aún no hay
  // atleta elegido.
  const cardProps = seleccion ? {
    competicion: data?.competicion ?? '',
    categoria,
    fecha: rieti
      ? undefined
      : modo === 'salida' && prueba?.hora
      ? [data?.fecha, prueba.hora].filter(Boolean).join(' · ')
      : (data?.fecha ?? undefined),
    filas: seleccion.runners,
    protagonistaEnFila: seleccion.protagonistaEnFila,
    formato, escala, degradado, opacidad, posicion, posH, branding: posicion,
    // EA (Europeo): si no hay foto de atleta, fondo oficial del Alexander
    // Stadium (mismo que las tarjetas automáticas), con el scrim al 50% para
    // que el contenido se lea encima (Santi 2026-08-10).
    fotoSrc: fotoSrc || (data?.fuente === 'ea' ? '/ea-birmingham26-fondo.jpg' : undefined),
    scrimColor: scrimColor ?? skinBg,
  } : null;

  // Genera el PNG en 2º plano cada vez que cambia la ficha, para tener el File
  // listo al pulsar Compartir (navigator.share exige activación de usuario; si
  // se generase dentro del click con await, iOS lo rechaza). Debounce para no
  // rasterizar en cada tick de slider.
  useEffect(() => {
    if (!seleccion) { setShareFile(null); return; }
    let cancel = false;
    setShareFile(null); // invalida el anterior mientras se regenera
    const t = setTimeout(() => {
      void renderCanvasBlob()
        .then((blob) => { if (!cancel) setShareFile(new File([blob], nombreArchivo(), { type: 'image/png' })); })
        .catch(() => { /* si falla, Compartir queda oculto; Descargar reintenta on-click */ });
    }, 450);
    return () => { cancel = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccion, formato, escala, degradado, opacidad, posicion, posH, fotoSrc, scrimColor]);

  return (
    // La INTERFAZ va SIEMPRE en retro (skin-retro fuerza el tema por encima de
    // lo que tenga el <html>); el estilo de la tarjeta se elige aparte.
    <div className="ld-shell skin-retro">
      <header className="ld-header">
        {/* El logo/encabezado lleva al inicio sin recargar (Riki 2026-07-11). */}
        <button
          type="button"
          className="ld-header__brand"
          onClick={volverInicio}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}
          aria-label="Volver al inicio"
        >
          <img className="ld-header__mark" src={SKIN_ICON.retro} alt="" width={38} height={38} />
          <span>Mundo Atletismo</span>
        </button>
        <div className="ld-header__user">
          {/* En admin (/telegram), vuelta al panel del campeonato (/malaga),
              a la derecha antes de los iconos (Santi 2026-07-22). */}
          {admin ? (
            <Link
              to={`/${edicionActiva().id}`}
              className="ld-btn ld-btn--ghost ld-btn--small"
              style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
              aria-label="Volver al panel del campeonato"
            >
              ← Panel
            </Link>
          ) : null}
          <a
            className="ld-icon-btn"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram @mund.oatletismo"
            title="Síguenos en Instagram"
          >
            <Icon name="instagram" size={20} />
          </a>
        </div>
      </header>

      <main className="ld-main">
        <div className="ld-stack ld-stack--lg">
          {/* Banner de bienvenida + CTA a redes (Riki 2026-07-14). */}
          <div className="ld-card" style={{ borderLeft: '4px solid var(--ld-accent)' }}>
            <h2 className="ld-card__title" style={{ margin: 0 }}>Crea tu publicación en segundos 🏅</h2>
            <p className="ld-card__hint" style={{ marginTop: 6 }}>
              Elige tu competición, búscate por nombre o prueba, y genera tu <strong>resultado</strong> o tu <strong>lista de salida</strong> con tu foto — lista para Instagram.
            </p>
            <p style={{ margin: '8px 0 0', fontWeight: 700, color: 'var(--ld-accent)' }}>
              📲 Súbela a tus stories y etiqueta a{' '}
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>@mund.oatletismo</a>
              {' '}— ¡las mejores las reposteamos!
            </p>
          </div>
          {/* Paso 1: elegir competición (en modo Rieti va fija, sin selector) */}
          {rieti ? (
          <div className="ld-card" style={{ padding: 0, overflow: 'hidden' }}>
            <img
              src="/rieti-cover.png"
              alt={rieti.campeonato.titulo}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              style={{ width: '100%', display: 'block', aspectRatio: '16 / 9', objectFit: 'cover' }}
            />
            <div style={{ padding: 16 }}>
              <h2 className="ld-card__title" style={{ margin: 0 }}>{rieti.campeonato.titulo}</h2>
              <p className="ld-card__hint" style={{ marginTop: 6 }}>
                {rieti.campeonato.fecha_inicio}–{rieti.campeonato.fecha_fin} · Búscate por tu nombre y crea tu publicación.
              </p>
              {loading ? <p className="ld-card__hint" style={{ marginTop: 8 }}>Cargando resultados…</p> : null}
              {error ? <div className="ld-login__error" style={{ marginTop: 12 }}>{error}</div> : null}
            </div>
          </div>
          ) : birmingham ? (
          // Portada de /Birmingham: logo magenta oficial sobre la foto del
          // Alexander Stadium (mismo assets que el acceso rápido de /resultados).
          <div className="ld-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              style={{
                width: '100%', aspectRatio: '16 / 9',
                backgroundImage: 'linear-gradient(rgba(10,12,20,0.4), rgba(10,12,20,0.55)), url(/birmingham-stadium.jpg)',
                backgroundSize: 'cover', backgroundPosition: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <img
                src="/birmingham-logo.png"
                alt="Campeonato de Europa · Birmingham 2026"
                style={{ width: '60%', maxHeight: '80%', objectFit: 'contain', filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.55))' }}
              />
            </div>
            <div style={{ padding: 16 }}>
              <h2 className="ld-card__title" style={{ margin: 0 }}>Campeonato de Europa · Birmingham 2026</h2>
              <p className="ld-card__hint" style={{ marginTop: 6 }}>
                10–16 ago 2026 · Búscate por tu nombre y crea tu publicación.
              </p>
              {loading ? <p className="ld-card__hint" style={{ marginTop: 8 }}>Cargando resultados…</p> : null}
              {error ? <div className="ld-login__error" style={{ marginTop: 12 }}>{error}</div> : null}
            </div>
          </div>
          ) : (
          <div className="ld-card">
            <h2 className="ld-card__title">1 · Elige tu competición</h2>
            <p className="ld-card__hint">
              Elige de la lista de la RFEA o pega la URL de tu competición (Conersys, FAM, RFEA Live, European Athletics).
            </p>

            {/* Accesos rápidos (Riki 2026-07-11; recorte Santi 2026-08-06:
                solo Cto. España absoluto + Mundial sub-20 + Europeo absoluto,
                fuera U20/U23/Madrid/Rieti/Lima). Click = seleccionarlo.
                Desaparecen en cuanto se elige/carga una competición. */}
            {!data && !loading && (campeonatos?.length ?? 0) > 0 ? (
              // auto-fit + minmax: en paralelo en pantalla ancha y 2×2 en
              // móvil, sin media queries (Riki 2026-07-11).
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 12 }}>
                {(campeonatos ?? [])
                  .filter((c) => /campeonatos? de espa[ñn]a/i.test(c.titulo) && !/sub|u\d\d|m[áa]ster/i.test(c.titulo))
                  .slice(0, 1)
                  .map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => onPickCampeonato(c.code)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 0,
                      background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center',
                      color: 'var(--ld-text)',
                    }}
                  >
                    {/* El marco se ve SIEMPRE (fondo del tema de respaldo):
                        si el cartel no carga —p.ej. backend sin desplegar—
                        queda el recuadro con el nombre centrado debajo
                        (ajuste Riki 2026-07-11). Con alt="" un <img> roto no
                        pinta icono, solo conserva su caja. */}
                    <img
                      src={cartelSrc(c.code)}
                      alt=""
                      loading="lazy"
                      style={{
                        width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 10,
                        border: '1px solid var(--ld-line, #444)', display: 'block',
                        background: 'linear-gradient(135deg, var(--ld-bg-grad-top, #1a1a1a), var(--ld-bg-grad-bot, #101010))',
                      }}
                    />
                    <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.25, width: '100%', textAlign: 'center', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {acortarCampeonato(c.titulo)}
                    </span>
                  </button>
                ))}
                {/* World Athletics (2026-07-29): Mundial sub-20 Oregón 2026
                    (5-9 ago). Lima 2024 (banco de pruebas) retirada del
                    selector 2026-08-06; se sigue pudiendo pegar su URL. */}
                <button
                  type="button"
                  onClick={() => void cargar('https://worldathletics.org/competitions/world-athletics-u20-championships/oregon26/timetable')}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 0,
                    background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center',
                    color: 'var(--ld-text)',
                  }}
                >
                  <img
                    src="/wa-u20-cover.png"
                    alt=""
                    loading="lazy"
                    style={{
                      width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 10,
                      border: '1px solid var(--ld-line, #444)', display: 'block',
                      background: 'linear-gradient(135deg, var(--ld-bg-grad-top, #1a1a1a), var(--ld-bg-grad-bot, #101010))',
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.25, width: '100%', textAlign: 'center', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    Mundial U20 · Oregon 2026
                  </span>
                </button>
                {/* European Athletics: Europeo absoluto — Birmingham 2026.
                    2026-08-11: RE-restringido a SOLO admin (/telegram) tras el
                    aviso de bloqueo de EA — en público, cada visitante que lo
                    pulsara llamaría a su API. Para reabrir al público: quitar
                    disabled/cursor/opacity de admin. ?code=ECH26 forzado (el
                    sondeo probaría EU18CH26/Rieti antes que ECH26). */}
                <button
                  type="button"
                  disabled={!admin}
                  title={admin ? undefined : 'No disponible temporalmente'}
                  onClick={() => void cargar(BIRMINGHAM_URL)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 0,
                    background: 'none', border: 'none', textAlign: 'center',
                    color: 'var(--ld-text)',
                    cursor: admin ? 'pointer' : 'not-allowed',
                    opacity: admin ? 1 : 0.45,
                  }}
                >
                  <div
                    style={{
                      width: '100%', aspectRatio: '16 / 9', borderRadius: 10, overflow: 'hidden',
                      border: '1px solid var(--ld-line, #444)',
                      backgroundImage: 'linear-gradient(rgba(10,12,20,0.45), rgba(10,12,20,0.55)), url(/birmingham-stadium.jpg)',
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/birmingham-logo.png"
                      alt=""
                      loading="lazy"
                      style={{ width: '72%', maxHeight: '82%', objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))' }}
                    />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.25, width: '100%', textAlign: 'center', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    Cto. Europa · Birmingham 2026
                  </span>
                </button>
              </div>
            ) : null}

            <div className="ld-row" style={{ marginTop: 8 }}>
              <select
                value=""
                disabled={loading || !authReady || campeonatos === null}
                onChange={(e) => onPickCampeonato(e.target.value)}
                style={{ flex: 1, minWidth: 220 }}
              >
                <option value="">
                  {!authReady || campeonatos === null
                    ? 'Cargando competiciones…'
                    : campeonatos.length ? 'Selecciona una competición…' : '(no se pudo cargar la lista)'}
                </option>
                {(campeonatos ?? []).map((c) => (
                  <option key={c.code} value={c.code}>{c.titulo} · {c.fecha_inicio}</option>
                ))}
              </select>
            </div>

            <form className="ld-row" style={{ marginTop: 12 }} onSubmit={(e) => { e.preventDefault(); void cargar(url); }}>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://rfealive.es/..."
                style={{ flex: 1 }}
              />
              <button type="submit" className="ld-btn ld-btn--primary" disabled={loading || !url.trim()}>
                {loading ? 'Cargando…' : 'Cargar'}
              </button>
            </form>

            {error ? <div className="ld-login__error" style={{ marginTop: 12 }}>{error}</div> : null}
          </div>
          )}

          {/* Paso 2: elegir prueba */}
          {data ? (
            <div className="ld-card">
              <div className="ld-spread">
                <div>
                  <p className="muted mono" style={{ margin: 0, fontSize: 11, letterSpacing: '0.06em' }}>
                    {data.fuente.toUpperCase()} · {data.fecha ?? '—'}
                  </p>
                  {/* "Búscate en tus pruebas" (Riki 2026-07-11): que se
                      entienda que puedes buscarte a ti mismo por nombre. */}
                  <h2 className="ld-card__title" style={{ margin: '4px 0 0' }}>2 · Búscate en tus pruebas</h2>
                </div>
                <span className="ld-pill ld-pill--muted">{pruebas.length} pruebas</span>
              </div>
              {/* ESPEJO de EA (Birmingham): el motor del directo en GCP no
                  puede leer EA (Cloudflare) — esta pestaña le bombea los
                  feeds. Toggle + Wake Lock + indicador de vida. */}
              {admin && data.fuente === 'ea' ? (
                <div className="ld-row" style={{ marginTop: 10, gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={`ld-btn ld-btn--small ${espejo?.activa ? 'ld-btn--primary' : 'ld-btn--ghost'}`}
                    onClick={() => void toggleEspejo()}
                  >
                    {espejo?.activa ? '⏸ Parar espejo del directo' : '▶ Espejo del directo (automático)'}
                  </button>
                  <span className="muted mono" style={{ fontSize: 11 }}>
                    {espejo?.activa
                      ? `sondeo ${espejo.ultimoCiclo ? new Date(espejo.ultimoCiclo).toLocaleTimeString('es-ES') : '…'} (cada 45 s) · último envío ${espejo.ultimoBombeo ? new Date(espejo.ultimoBombeo).toLocaleTimeString('es-ES') : '—'} · ${espejo.unidadesEspejadas} unidades${espejo.error ? ` · ⚠ ${espejo.error}` : ''}`
                      : 'El automático del canal lee lo que esta pestaña bombea: déjala abierta y VISIBLE durante las sesiones.'}
                  </span>
                </div>
              ) : null}
              {/* Modo (Riki 2026-07-14): resultado de una prueba competida, o
                  lista de salida de una prueba por disputarse. */}
              <div className="ld-row" style={{ gap: 8, marginTop: 10 }}>
                <SegBtn on={modo === 'resultado'} onClick={() => { setModo('resultado'); setPrueba(null); setCampo(null); setProtagIdx(null); setPruebaColapsada(false); }}>Resultado</SegBtn>
                <SegBtn on={modo === 'salida'} onClick={() => { setModo('salida'); setPrueba(null); setCampo(null); setProtagIdx(null); setPruebaColapsada(false); }}>Lista de salida</SegBtn>
              </div>
              {/* Al elegir prueba, el Paso 2 colapsa a un resumen (Riki
                  2026-07-14); "Cambiar" reabre el buscador y la lista. */}
              {prueba && pruebaColapsada ? (
                <div className="ld-row" style={{ marginTop: 12, gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700 }}>{prueba.nombre}{prueba.ronda ? ` · ${prueba.ronda}` : ''}</span>
                  <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setPruebaColapsada(false)}>Cambiar</button>
                </div>
              ) : (
              <>
              <div className="ld-row" style={{ marginTop: 12, position: 'relative' }}>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setPagina(0); }}
                  placeholder="Buscar por prueba o por tu nombre…"
                  style={{ flex: 1 }}
                />
                {query ? <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setQuery('')}>Limpiar</button> : null}
                {/* Autocompletado EMERGENTE (flota bajo el buscador, no empuja
                    el contenido): "atleta — prueba · ronda" desde el 5º
                    carácter. Click → abre la prueba y te preselecciona. */}
                {sugerencias.length ? (
                  <div
                    style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                      marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2,
                      background: 'var(--ld-bg, #111)', border: '1px solid var(--ld-line, #444)',
                      borderRadius: 8, padding: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                      maxHeight: 320, overflowY: 'auto',
                    }}
                  >
                    {sugerencias.map((s, i) => (
                      <button
                        key={`${s.athId}__${s.ev.detalleKey}__${i}`}
                        type="button"
                        className="ld-btn ld-btn--ghost ld-btn--small"
                        onClick={() => { setSugerencias([]); void pickPrueba(s.ev, s.athId); }}
                        style={{ textAlign: 'left', justifyContent: 'flex-start', width: '100%' }}
                      >
                        <span style={{ fontWeight: 700 }}>{s.nombre}</span>
                        <span className="muted" style={{ marginLeft: 8 }}>
                          — {s.ev.nombre} · {s.ev.ronda || '—'}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="ld-stack" style={{ marginTop: 12 }}>
                {pruebasVisibles.map((ev, i) => {
                  const conClasif = admin && modo === 'resultado' && rondaClasificable(ev);
                  // Sin españoles (prueba entera o ronda a la que no pasaron):
                  // fila atenuada y publicación desactivada (EA/WA).
                  const sinEsp = !!ev.detalleKey && espMap[ev.detalleKey] === false;
                  return (
                  // El wrapper posiciona los botones del modo admin SOBRE la
                  // fila (un <button> no puede anidar otro botón).
                  <div key={`${ev.pruebaId ?? ev.nombre}__${ev.ronda}__${ev.hora}__${i}`} style={{ position: 'relative', opacity: sinEsp ? 0.4 : undefined }}>
                  <button
                    type="button"
                    className={`ld-event ld-event--btn ${prueba === ev ? 'ld-event--active' : ''}`}
                    onClick={() => (esKeyResumenWA(ev.detalleKey) ? setResumenKey(ev.detalleKey!) : void pickPrueba(ev))}
                   
                    title={sinEsp ? (admin ? 'Sin españoles en esta ronda — no se publica' : 'Sin españoles en esta ronda') : undefined}
                    style={{ textAlign: 'left', cursor: sinEsp ? 'not-allowed' : 'pointer', width: '100%', paddingRight: admin && modo === 'resultado' ? (conClasif ? 214 : 118) : undefined }}
                  >
                    <div className="ld-event__head">
                      <span className="ld-event__name">{ev.nombre}</span>
                      {/* En rfea.es (detalleKey) el listado solo trae el top-3.
                          Si ya bajamos el detalle RES de esta unidad (búsqueda
                          o prueba abierta), enseñamos el nº EXACTO; si no, "3+". */}
                      <span className="ld-event__meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {ev.ronda || '—'} · {ev.hora || '—'} ·{' '}
                        {ev.detalleKey && conteos[ev.detalleKey] !== undefined
                          ? conteos[ev.detalleKey]
                          : `${campoDe(ev).length}${ev.detalleKey && campoDe(ev).length >= 3 ? '+' : ''}`} atletas
                        {/* Estado de la prueba con color (Riki 2026-07-11). */}
                        {ev.estado ? <span style={estadoPillStyle(ev.estado)}>{ev.estado}</span> : null}
                      </span>
                    </div>
                    {rieti ? (
                      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ld-text-muted)', lineHeight: 1.35 }}>
                        {campoDe(ev).map((p) => p.nombre).join(' · ')}
                      </div>
                    ) : null}
                  </button>
                  {/* ADMIN: publicar ESTA serie — un botón por canal (el de
                      WhatsApp separado a propósito: control fino del gasto
                      con el límite del trial de Whapi); y si la ronda ya
                      está completa, acceso directo a sus CLASIFICADOS (Q/q)
                      sin tener que entrar en la serie. */}
                  {admin && modo === 'resultado' && esKeyResumenWA(ev.detalleKey) ? (
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="ld-btn ld-btn--primary ld-btn--small"
                        title="Publicar el resumen de los españoles en Instagram (Make)"
                        onClick={(e) => { e.stopPropagation(); setResumenKey(ev.detalleKey!); }}
                      >
                        Instagram
                      </button>
                    </span>
                  ) : admin && modo === 'resultado' ? (
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 6 }}>
                      {conClasif ? (
                        <button
                          type="button"
                          className="ld-btn ld-btn--ghost ld-btn--small"
                          title="Publicar los clasificados de la ronda (Q/q)"
                         
                          onClick={(e) => { e.stopPropagation(); setTgEvento({ ev, modo: 'clasificados', canal: 'whatsapp' }); }}
                        >
                          Q/q
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="ld-btn ld-btn--primary ld-btn--small"
                       
                        onClick={(e) => { e.stopPropagation(); setTgEvento({ ev, modo: 'serie', canal: 'telegram' }); }}
                      >
                        Telegram
                      </button>
                      <button
                        type="button"
                        className="ld-btn ld-btn--ghost ld-btn--small"
                        title="Publicar en el canal de WhatsApp (Whapi — ojo al límite del trial)"
                       
                        onClick={(e) => { e.stopPropagation(); setTgEvento({ ev, modo: 'serie', canal: 'whatsapp' }); }}
                      >
                        WhatsApp
                      </button>
                    </span>
                  ) : null}
                  </div>
                  );
                })}
              </div>
              {modo === 'salida' && pruebas.length === 0 ? (
                <p className="ld-card__hint" style={{ marginTop: 12 }}>
                  No apareces en ninguna lista de salida. Espera a que se publiquen las listas o comprueba los resultados con el modo «Resultado».
                </p>
              ) : null}
              {totalPaginas > 1 ? (
                <div className="ld-row" style={{ marginTop: 12, justifyContent: 'center', alignItems: 'center', gap: 12 }}>
                  <button
                    type="button"
                    className="ld-btn ld-btn--ghost ld-btn--small"
                    disabled={pag === 0}
                    onClick={() => setPagina(pag - 1)}
                  >
                    ‹ Anteriores
                  </button>
                  <span className="muted mono" style={{ fontSize: 12 }}>{pag + 1} / {totalPaginas}</span>
                  <button
                    type="button"
                    className="ld-btn ld-btn--ghost ld-btn--small"
                    disabled={pag >= totalPaginas - 1}
                    onClick={() => setPagina(pag + 1)}
                  >
                    Siguientes ›
                  </button>
                </div>
              ) : null}
              </>
              )}
            </div>
          ) : null}

          {/* Paso 3: autoselección del atleta en el campo completo */}
          {prueba ? (
            <div className="ld-card" ref={paso3Ref}>
              <h2 className="ld-card__title">3 · Encuéntrate</h2>
              <p className="ld-card__hint">Toca tu nombre en la clasificación de {prueba.nombre}.</p>
              {cruzando ? (
                <p className="muted" style={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                  <span className="ld-spinner" aria-hidden /> Calculando marcas y etiquetas…
                </p>
              ) : campo ? (
                protagIdx !== null && listaColapsada ? (
                  <div className="ld-row" style={{ marginTop: 8, gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700 }}>
                      <span className="mono muted" style={{ marginRight: 8 }}>#{campo[protagIdx].puesto}</span>
                      {campo[protagIdx].nombre}
                    </span>
                    <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setListaColapsada(false)}>Cambiar</button>
                  </div>
                ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 8 }}>
                  {/* Dos columnas para no tener que bajar tanto con muchos
                      atletas (hasta ~15 por prueba). Riki 2026-07-14. */}
                  {campo.map((p, i) => (
                    <button
                      key={`${p.puesto}__${p.nombre}__${i}`}
                      type="button"
                      className={`ld-event ld-event--btn ${protagIdx === i ? 'ld-event--active' : ''}`}
                      onClick={() => pickAtleta(i)}
                      style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}
                    >
                      <div className="ld-event__head" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <span className="mono" style={{ minWidth: 28 }}>#{p.puesto}</span>
                        <span className="ld-event__name" style={{ flex: 1 }}>{p.nombre}</span>
                        {/* Etiquetas del cruce (PB/SB/récord/mínima/líder…), ya
                            calculadas por cruzarCampo — pintarlas es gratis. */}
                        {p.etiquetas?.length ? (
                          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {p.etiquetas.map((et) => (
                              <span key={et} className="ld-pill ld-pill--muted" style={{ fontSize: 10 }}>{et}</span>
                            ))}
                          </span>
                        ) : null}
                        <span className="mono">{p.marca}</span>
                        {/* Q = clasifica por plazas; q = por tiempos (solo
                            rondas previas; la fuente rellena las "q" cuando
                            todas las series están en Oficial). */}
                        {p.quali ? <span className="mono" style={{ color: 'var(--ld-accent)', fontWeight: 700 }}>{p.quali}</span> : null}
                      </div>
                    </button>
                  ))}
                </div>
                )
              ) : null}
            </div>
          ) : null}

          {/* Paso 4: ficha protagonista + vecinos + descarga */}
          {seleccion ? (
            <div className="ld-card" ref={paso4Ref}>
              {/* En móvil los 3 botones de foto no caben junto al título → con
                  flexWrap bajan a una línea propia debajo (Riki 2026-07-14). */}
              <div className="ld-spread" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                <h2 className="ld-card__title" style={{ margin: 0 }}>4 · Tu publicación</h2>
                <div className="ld-row" style={{ flexWrap: 'wrap' }}>
                  <input ref={fileInput} type="file" accept="image/*" hidden onChange={onPickFoto} />
                  <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => fileInput.current?.click()}>
                    {fotoDataUrl ? 'Cambiar foto' : 'Subir foto'}
                  </button>
                  {fotoDataUrl ? (
                    <>
                      <button
                        type="button"
                        className="ld-btn ld-btn--small ld-btn--ghost"
                        onClick={() => setEncuadreAbierto(true)}
                      >
                        Encuadrar imagen
                      </button>
                      <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setFotoDataUrl(undefined)}>Quitar foto</button>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Encuadre a PANTALLA COMPLETA que AÍSLA la edición (Riki
                  2026-07-14): se abre solo al subir la foto y con "Encuadrar
                  imagen"; "Listo" lo cierra. La foto se mueve cruda (CSS), por
                  eso va fluido. */}
              {fotoDataUrl && encuadreAbierto ? (
                <div
                  style={{
                    position: 'fixed', inset: 0, zIndex: 100,
                    background: 'rgba(0,0,0,0.9)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                    gap: 14, padding: 16, overflowY: 'auto',
                  }}
                >
                  <p className="mono" style={{ color: '#fff', fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', margin: 0 }}>
                    Encuadra tu foto
                  </p>
                  <div style={{ position: 'relative', width: '100%', maxWidth: 'min(340px, calc((100vh - 180px) * 9 / 16))' }}>
                    <ImageEditor
                      imageUrl={fotoDataUrl} width={w} height={h}
                      offX={fotoX} offY={fotoY} zoom={fotoZoom}
                      setOffX={setFotoX} setOffY={setFotoY} setZoom={setFotoZoom}
                    />
                    {/* Overlay con los TEXTOS reales (transparente, no capta
                        eventos) para ver cómo queda el encuadre bajo el bloque. */}
                    {cardProps ? (
                      <div className={rieti ? 'skin-rieti' : `skin-${cardSkin}`} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                        <Preview w={w} h={h}>
                          <FichaVivoCard {...cardProps} overlay overlayPart="datos" />
                        </Preview>
                      </div>
                    ) : null}
                    {/* "Listo" SOBRE la imagen (degradado abajo) para que SIEMPRE
                        se vea, incluso en móviles donde el modal se salía y no se
                        podía cerrar (Riki 2026-07-14). El degradado no capta
                        eventos; solo el botón. */}
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '36px 12px 12px', display: 'flex', justifyContent: 'center', pointerEvents: 'none', background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)', borderRadius: '0 0 var(--ld-radius-md) var(--ld-radius-md)' }}>
                      <button type="button" className="ld-btn ld-btn--primary" style={{ minWidth: 160, pointerEvents: 'auto' }} onClick={() => setEncuadreAbierto(false)}>
                        Listo
                      </button>
                    </div>
                  </div>
                  <p className="mono" style={{ color: '#bbb', textAlign: 'center', margin: 0, fontSize: 12 }}>
                    Arrastra para mover · rueda o pellizco para hacer zoom
                  </p>
                </div>
              ) : null}

              {/* Selector de ESTILO de la TARJETA (Riki 2026-07-14): la web va
                  siempre en retro; aquí el atleta elige colores+fuentes de su
                  imagen. Solo afecta a la tarjeta (envuelta abajo en skin-*). */}
              {!rieti ? (
              <div className="ld-row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 70 }}>ESTILO</span>
                {SKIN_IDS.map((s) => (
                  <SegBtn key={s} on={cardSkin === s} onClick={() => setCardSkin(s)}>{SKIN_LABEL[s]}</SegBtn>
                ))}
              </div>
              ) : null}

              <div className="ld-row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 70 }}>POSICIÓN</span>
                <SegBtn on={posicion === 'arriba'} onClick={() => setPosicion('arriba')}>Arriba</SegBtn>
                <SegBtn on={posicion === 'abajo'} onClick={() => setPosicion('abajo')}>Abajo</SegBtn>
              </div>

              {/* RIVALES: "Con" muestra los vecinos de contexto; "Sin" deja solo
                  al atleta buscado (Riki 2026-07-14). Igual en resultado y salida. */}
              <div className="ld-row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 70 }}>RIVALES</span>
                <SegBtn on={!soloYo} onClick={() => setSoloYo(false)}>Con</SegBtn>
                <SegBtn on={soloYo} onClick={() => setSoloYo(true)}>Sin</SegBtn>
              </div>

              <label className="ld-row" style={{ gap: 12, marginBottom: 12 }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 70 }}>TAMAÑO</span>
                <input type="range" min={0} max={100} step={5} value={Math.round((escala - 1) / 0.4 * 100)} onChange={(e) => setEscala(1 + (parseInt(e.target.value, 10) / 100) * 0.4)} style={{ flex: 1 }} />
                <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{Math.round((escala - 1) / 0.4 * 100)}%</span>
              </label>

              <label className="ld-row" style={{ gap: 12, marginBottom: 12 }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 70 }}>OPACIDAD</span>
                <input type="range" min={0} max={100} step={1} value={opacidad} onChange={(e) => setOpacidad(parseInt(e.target.value, 10))} style={{ flex: 1 }} />
                <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{opacidad}%</span>
              </label>

              <label className="ld-row" style={{ gap: 12, marginBottom: 12 }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 70 }}>DEGRADADO</span>
                <input type="range" min={0} max={100} step={1} value={degradado} onChange={(e) => setDegradado(parseInt(e.target.value, 10))} style={{ flex: 1 }} />
                <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{degradado}%</span>
              </label>

              <div className="ld-row" style={{ gap: 12, marginBottom: 12, alignItems: 'center' }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 70 }}>FONDO</span>
                <input
                  type="color"
                  value={scrimColor ?? skinBg}
                  onChange={(e) => setScrimColor(e.target.value)}
                  style={{ width: 40, height: 28, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                />
                <span className="mono" style={{ fontSize: 11, flex: 1 }}>{scrimColor ?? `${skinBg} (skin)`}</span>
                {scrimColor ? (
                  <SegBtn on={false} onClick={() => setScrimColor(null)}>Auto</SegBtn>
                ) : null}
              </div>

              {cardProps ? (
                // La TARJETA lleva el estilo elegido (skin-*), independiente de la
                // web (retro): envuelve el preview y el nodo de captura para que la
                // imagen exportada salga con esos colores y fuentes.
                <div className={rieti ? 'skin-rieti' : `skin-${cardSkin}`}>
                  <Preview w={w} h={h}><FichaVivoCard {...cardProps} /></Preview>
                  {/* Nodo a tamaño real fuera de pantalla para html2canvas. */}
                  <div aria-hidden style={{ position: 'fixed', left: -100000, top: 0, pointerEvents: 'none' }}>
                    <FichaVivoCard ref={cardRef} {...cardProps} />
                  </div>
                </div>
              ) : null}

              <div className="ld-row" style={{ marginTop: 14, gap: 10 }}>
                {esMovil ? (
                  <>
                    {/* Móvil: UN botón de compartir (WhatsApp + Instagram) +
                        descargar. Abre el menú nativo con imagen + texto del
                        resultado. En WhatsApp llega el texto; en Instagram
                        Stories no (Stories solo coge la imagen). */}
                    <button
                      type="button"
                      className="ld-btn ld-btn--primary"
                      disabled={busy}
                      onClick={() => void compartirWhatsApp()}
                      aria-label="Compartir en WhatsApp o Instagram"
                      title="Compartir (WhatsApp / Instagram)"
                      style={{ flex: 1, justifyContent: 'center', gap: 8 }}
                    >
                      <IconWhatsApp /> <IconInstagram />
                    </button>
                    <button
                      type="button"
                      className="ld-btn ld-btn--ghost"
                      disabled={busy}
                      onClick={() => void descargar()}
                      aria-label="Descargar imagen"
                      title="Descargar"
                      style={{ flex: 1, justifyContent: 'center' }}
                    >
                      {busy ? <span className="ld-spinner" aria-hidden /> : <IconDownload />}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ld-btn ld-btn--primary"
                      disabled={busy}
                      onClick={() => void compartirWhatsApp()}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                    >
                      <IconWhatsApp /> <IconInstagram /> Compartir
                    </button>
                    <button type="button" className="ld-btn ld-btn--ghost" disabled={busy} onClick={() => void descargar()}>
                      {busy ? (<><span className="ld-spinner" aria-hidden />Generando…</>) : 'Descargar imagen'}
                    </button>
                  </>
                )}
              </div>
              {!esMovil ? (
                <p className="faint mono" style={{ margin: '10px 0 0', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <IconInstagram size={14} /> Para tus Stories de Instagram: descarga la imagen y súbela desde el móvil (Instagram no permite publicar en Stories desde el ordenador).
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </main>

      {/* ADMIN: modal de publicación de la serie elegida (Telegram o WhatsApp
          según el botón de la tarjeta). */}
      {admin && tgEvento && data ? (
        <TelegramPublishModal
          data={data}
          ev={tgEvento.ev}
          modoInicial={tgEvento.modo}
          detalleDe={detalleDe}
          compUrl={url}
          canal={tgEvento.canal}
          onClose={() => setTgEvento(null)}
        />
      ) : null}
      {/* Resumen de jornada de los españoles (WA) → Instagram. */}
      {admin && resumenKey ? (
        <ResumenJornadaModal resKey={resumenKey} onClose={() => setResumenKey(null)} />
      ) : null}
    </div>
  );
}

function SegBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className={`ld-btn ld-btn--small ${on ? 'ld-btn--primary' : 'ld-btn--ghost'}`} onClick={onClick}>
      {children}
    </button>
  );
}
